import { CategoryAreaPinComponentSettings } from '@lessonup/pins-shared';
import { color, styled } from '@lessonup/ui-components';
import { addImageSizing } from '@lessonup/utils';
import React, { useEffect } from 'react';
import { TipTapComponent } from '../../components/Tiptap/Tiptap.utils';
import { colorSets } from '../../foundations/colorSets/colorSets';
import { OnMouseDownHandler } from '../../hooks/useHandleMouseEvents/useHandleMouseEvents.types';
import {
  convertToPercentagePosition,
  convertToPixelPosition,
  isUsingPercentages,
} from '../../utils/pinComponents/categoryComponentActions';
import { useEffectivePinComponentSettings } from '../../utils/pinTheme/PinThemeContext';
import { useUpdatePin } from '../../utils/updatePinContext/updatePinContext';
import Category, { CategorySettings } from './Category';
import { useCategoryAreaHandlers } from './hooks/useCategoryAreaHandlers';
import { useContainerSize } from './hooks/useContainerSize';

export interface CategoryAreaPinComponentProps {
  componentId: string;
  pinId: string;
  settings: CategoryAreaPinComponentSettings;
  editable: boolean;
  active: boolean;
  scale?: number;
  activeCategoryId?: string;
  onMouseDown?: OnMouseDownHandler;
  isTextEditMode?: boolean;
  setModeTextEdit?: (componentId: string, categoryId: string) => void;
  setModeCategoryEdit?: (componentId: string, categoryId: string) => void;

  Renderer?: TipTapComponent;
}

export const CategoryAreaPinComponent: React.FC<CategoryAreaPinComponentProps> = ({
  componentId,
  pinId,
  settings,
  editable,
  active,
  scale = 1,
  activeCategoryId,
  onMouseDown,
  isTextEditMode = false,
  setModeTextEdit,
  setModeCategoryEdit,
  Renderer,
}) => {
  const dispatch = useUpdatePin();
  const { containerRef, containerSize } = useContainerSize({ scale });
  const categoryAreaSettings = useEffectivePinComponentSettings('CATEGORY_AREA', {
    settings: settings,
  });

  const { categories, layoutMode } = categoryAreaSettings;

  useEffect(() => {
    if (layoutMode === 'CUSTOM') {
      const updatedCategories = categories.map((category) => convertToPercentagePosition(category, containerSize));

      const needsUpdate = categories.some((category, index) => {
        if (isUsingPercentages(category)) return false;

        const updatedCategory = updatedCategories[index];
        return (
          category.layout.position.x !== updatedCategory.layout.position.x ||
          category.layout.position.y !== updatedCategory.layout.position.y ||
          category.layout.size.width !== updatedCategory.layout.size.width ||
          category.layout.size.height !== updatedCategory.layout.size.height ||
          category.layout.rotation !== updatedCategory.layout.rotation
        );
      });

      if (needsUpdate) {
        dispatch({
          type: 'updatePinComponentSettings',
          pinComponentId: componentId,
          settings: {
            categories: updatedCategories,
          },
        });
      }
    }
  }, [layoutMode, categories, componentId, dispatch, containerSize]);
  const { handleCategoryMouseDown, handleCategoryMouseMove, handleCategoryMouseUp, handleCategoryContextMenu } =
    useCategoryAreaHandlers({
      componentId,
      active,
      editable,
      activeCategoryId,
      isTextEditMode,
      layoutMode,
      onMouseDown,
      setModeCategoryEdit,
      setModeTextEdit,
    });

  const renderCategories = () => {
    return categories.map((category: CategorySettings) => {
      const isActive = activeCategoryId === category.id;
      const isCategoryInTextEditMode = isTextEditMode && isActive;

      if (layoutMode === 'AUTO') {
        return (
          <StyledAutoCategory key={category.id} $editable={editable} $isCategoryAreaActive={active}>
            <Category
              category={category}
              active={isActive}
              editable={editable}
              layoutMode={layoutMode}
              isCategoryAreaActive={active}
              onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => handleCategoryMouseDown(event, category.id)}
              onMouseMove={(event: React.MouseEvent<HTMLDivElement>) => handleCategoryMouseMove(event, category.id)}
              onMouseUp={(event: React.MouseEvent<HTMLDivElement>) => handleCategoryMouseUp(event, category.id)}
              onContextMenu={(event: React.MouseEvent<HTMLDivElement>) => handleCategoryContextMenu(event, category.id)}
              pinId={pinId}
              isTextEditMode={isCategoryInTextEditMode}
              Renderer={Renderer}
              registerEditor={undefined}
              componentId={componentId}
              setModeTextEdit={setModeTextEdit}
            />
          </StyledAutoCategory>
        );
      }

      const pixelCategory = convertToPixelPosition(category, containerSize);
      const { position, size, rotation } = pixelCategory.layout;

      return (
        <StyledCustomCategory
          key={category.id}
          style={{
            left: position.x,
            top: position.y,
            width: size.width,
            height: size.height,
            transform: `rotate(${rotation}deg)`,
            zIndex: isActive ? 1 : 'unset',
          }}
          editable={editable}
          $isCategoryAreaActive={active}
        >
          <Category
            category={category}
            active={isActive}
            editable={editable}
            layoutMode={layoutMode}
            isCategoryAreaActive={active}
            onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => handleCategoryMouseDown(event, category.id)}
            onMouseMove={(event: React.MouseEvent<HTMLDivElement>) => handleCategoryMouseMove(event, category.id)}
            onMouseUp={(event: React.MouseEvent<HTMLDivElement>) => handleCategoryMouseUp(event, category.id)}
            onContextMenu={(event: React.MouseEvent<HTMLDivElement>) => handleCategoryContextMenu(event, category.id)}
            pinId={pinId}
            isTextEditMode={isCategoryInTextEditMode}
            Renderer={Renderer}
            registerEditor={undefined}
            componentId={componentId}
            setModeTextEdit={setModeTextEdit}
          />
        </StyledCustomCategory>
      );
    });
  };

  return (
    <StyledCategoryAreaWrapper
      $editable={editable}
      $active={active}
      $backgroundColor={categoryAreaSettings.backgroundColor}
      $backgroundImage={categoryAreaSettings.backgroundImage || undefined}
      $borderColor={categoryAreaSettings.borderColor}
      $borderWidth={categoryAreaSettings.borderSize}
      $borderStyle={categoryAreaSettings.borderStyle}
      $borderRadius={categoryAreaSettings.borderRadius}
    >
      <StyledCategoryArea
        ref={(el) => {
          containerRef.current = el;
        }}
        $layoutMode={layoutMode}
      >
        {renderCategories()}
      </StyledCategoryArea>
    </StyledCategoryAreaWrapper>
  );
};

const StyledCategoryAreaWrapper = styled.div<{
  $editable: boolean;
  $active: boolean;
  $backgroundColor?: string;
  $backgroundImage?: string;
  $borderColor?: string;
  $borderWidth?: number;
  $borderStyle?: string;
  $borderRadius?: number;
}>`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: ${(props) => props.$backgroundColor || color.neutral.surface.background};
  background-image: ${(props) =>
    props.$backgroundImage ? `url(${addImageSizing({ url: props.$backgroundImage, size: 'xl' })})` : 'none'};
  background-size: cover;
  background-position: center;
  border: ${(props) => {
    const width = props.$borderWidth || 0;
    const borderColor = props.$borderColor || color.neutral.outline.background;
    const style = props.$borderStyle || 'solid';
    return width > 0 ? `${width}px ${style} ${borderColor}` : 'none';
  }};
  border-radius: ${(props) => props.$borderRadius || 0}px;

  ${(props) =>
    props.$editable &&
    props.$active &&
    `
    outline: 1px solid ${color.accent.secondary.background};
    `}
`;

const StyledCategoryArea = styled.div<{ $layoutMode: 'AUTO' | 'CUSTOM' }>`
  width: 100%;
  height: 100%;
  position: relative;

  ${(props) =>
    props.$layoutMode === 'AUTO' &&
    `
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px;
  `}
`;

const StyledAutoCategory = styled.div<{ $editable?: boolean; $isCategoryAreaActive?: boolean }>`
  flex: 1 1 calc(25% - 6px);
  min-width: 100px;
  min-height: 50px;
  cursor: ${(props) => (props.$editable ? 'pointer' : 'default')};

  ${(props) =>
    props.$editable &&
    props.$isCategoryAreaActive &&
    `
    &:hover {
      outline: 1px solid ${colorSets.disabled.strokeSubjacent};
    }
  `}
`;

const StyledCustomCategory = styled.div<{ editable: boolean; $isCategoryAreaActive?: boolean }>`
  position: absolute;
  cursor: ${({ editable }) => (editable ? 'move' : 'default')};

  ${(props) =>
    props.editable &&
    props.$isCategoryAreaActive &&
    `
    &:hover {
      outline: 1px solid ${colorSets.disabled.strokeSubjacent};
    }
  `}
`;
